import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Badge, Input, Select, Modal, Grid, StatCard, PageHeader } from '../../ui/components';
import { RefreshCw, Eye, CircleCheck } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';
import { supabase } from '../../lib/supabase';

const statusColors = {
  pending: 'warning',
  accepted: 'success',
  rejected: 'danger',
  withdrawn: 'neutral',
  expired: 'neutral',
};

const statusLabels = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
};

const LenderBids = () => {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBid, setSelectedBid] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    fetchBids();
  }, []);

  useEffect(() => {
    filterBids();
  }, [bids, searchTerm, statusFilter]);

  const fetchBids = async () => {
    setLoading(true);
    try {
      // In production, filter by lender_id
      const { data, error } = await supabase
        .from('lender_bids')
        .select('*, loan_applications(id, anonymous_id, loan_amount, product_type, readiness_score, created_at)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBids(data || []);
    } catch (error) {
      console.error('Error fetching bids:', error);
      // Use empty array for demo
      setBids([]);
    } finally {
      setLoading(false);
    }
  };

  const filterBids = () => {
    let filtered = bids;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(bid => bid.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(bid =>
        bid.loan_applications?.anonymous_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bid.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBids(filtered);
  };

  const handleViewDetails = (bid) => {
    setSelectedBid(bid);
    setDetailsOpen(true);
  };

  const handleWithdraw = async (bidId) => {
    if (!confirm('Are you sure you want to withdraw this bid?')) return;

    setWithdrawing(true);
    try {
      const { error } = await supabase
        .from('lender_bids')
        .update({ status: 'withdrawn', updated_at: new Date().toISOString() })
        .eq('id', bidId);

      if (error) throw error;

      await fetchBids();
      setDetailsOpen(false);
      alert('Bid withdrawn successfully');
    } catch (error) {
      console.error('Error withdrawing bid:', error);
      alert('Failed to withdraw bid. Please try again.');
    } finally {
      setWithdrawing(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatRate = (rate) => {
    return `${(rate || 0).toFixed(3)}%`;
  };

  const calculateMonthlyPayment = (loanAmount, rate, termYears = 30) => {
    const principal = loanAmount;
    const monthlyRate = rate / 100 / 12;
    const numPayments = termYears * 12;
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    return payment;
  };

  const DetailRow = ({ label, children }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--lk-border-subtle)' }}>
      <span style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{label}</span>
      <span style={{ fontWeight: 500, fontSize: 'var(--lk-text-sm)' }}>{children}</span>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="My Bids"
        description="Track your submitted bids and their status"
        actions={<Button variant="primary" icon={RefreshCw} onClick={fetchBids}>Refresh</Button>}
      />

      {/* Stats Cards */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <StatCard label="Total Bids" value={bids.length} />
        <StatCard label="Pending" value={bids.filter(b => b.status === 'pending').length} />
        <StatCard label="Accepted" value={bids.filter(b => b.status === 'accepted').length} />
        <StatCard label="Win Rate" value={`${bids.length > 0 ? Math.round((bids.filter(b => b.status === 'accepted').length / bids.length) * 100) : 0}%`} />
      </Grid>

      {/* Filters */}
      <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--lk-space-3)', alignItems: 'center' }}>
          <Input
            placeholder="Search by application ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="withdrawn">Withdrawn</option>
            <option value="expired">Expired</option>
          </Select>
          <div style={{ textAlign: 'right', color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
            Showing {filteredBids.length} of {bids.length}
          </div>
        </div>
      </Surface>

      {/* Bids Table */}
      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Product</th>
                <th style={{ textAlign: 'right' }}>Loan Amount</th>
                <th style={{ textAlign: 'right' }}>Your Rate</th>
                <th style={{ textAlign: 'right' }}>Est. Payment</th>
                <th>Bid Date</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 'var(--lk-space-8)' }}>
                    <div className="lk-spinner lk-spinner-sm" style={{ display: 'inline-block', marginRight: 8 }} />
                    Loading bids...
                  </td>
                </tr>
              ) : filteredBids.length > 0 ? (
                filteredBids.map((bid) => {
                  const app = bid.loan_applications;
                  const monthlyPayment = app?.loan_amount 
                    ? calculateMonthlyPayment(app.loan_amount, bid.interest_rate)
                    : 0;

                  return (
                    <tr key={bid.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {app?.anonymous_id || `LK-${bid.application_id?.slice(0, 8).toUpperCase()}`}
                      </td>
                      <td>
                        <Badge variant="neutral">
                          {app?.product_type?.replace(/_/g, ' ') || '-'}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(app?.loan_amount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--lk-primary-600)' }}>{formatRate(bid.interest_rate)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--lk-text-tertiary)' }}>{formatCurrency(monthlyPayment)}/mo</td>
                      <td style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{formatDate(bid.created_at)}</td>
                      <td>
                        <Badge variant={statusColors[bid.status] || 'neutral'}>
                          {statusLabels[bid.status] || bid.status}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewDetails(bid)} />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-8)' }}>
                    {searchTerm || statusFilter !== 'all'
                      ? 'No bids match your filters'
                      : 'No bids yet. Visit the Marketplace to place your first bid!'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Surface>

      {/* Details Modal */}
      <Modal open={detailsOpen} onClose={() => setDetailsOpen(false)} title="Bid Details" size="lg">
        {selectedBid && (
          <>
            <Grid cols={2} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
              <Surface padding="var(--lk-space-4)">
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Application Info</div>
                <DetailRow label="Application ID">
                  {selectedBid.loan_applications?.anonymous_id || selectedBid.application_id?.slice(0, 8)}
                </DetailRow>
                <DetailRow label="Product">
                  {selectedBid.loan_applications?.product_type?.replace(/_/g, ' ') || '-'}
                </DetailRow>
                <DetailRow label="Loan Amount">
                  {formatCurrency(selectedBid.loan_applications?.loan_amount)}
                </DetailRow>
                <DetailRow label="Readiness Score">
                  <Badge variant={
                    selectedBid.loan_applications?.readiness_score >= 80 ? 'success' :
                    selectedBid.loan_applications?.readiness_score >= 60 ? 'warning' : 'danger'
                  }>
                    {selectedBid.loan_applications?.readiness_score || 0}
                  </Badge>
                </DetailRow>
              </Surface>
              <Surface padding="var(--lk-space-4)">
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Your Bid</div>
                <DetailRow label="Interest Rate">
                  <span style={{ color: 'var(--lk-primary-600)', fontWeight: 700 }}>{formatRate(selectedBid.interest_rate)}</span>
                </DetailRow>
                <DetailRow label="Est. Monthly Payment">
                  {formatCurrency(calculateMonthlyPayment(selectedBid.loan_applications?.loan_amount, selectedBid.interest_rate))}/mo
                </DetailRow>
                <DetailRow label="Bid Date">{formatDate(selectedBid.created_at)}</DetailRow>
                <DetailRow label="Status">
                  <Badge variant={statusColors[selectedBid.status]}>
                    {statusLabels[selectedBid.status]}
                  </Badge>
                </DetailRow>
              </Surface>
            </Grid>

            {selectedBid.notes && (
              <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-2)' }}>Notes</div>
                <p style={{ color: 'var(--lk-text-tertiary)', margin: 0 }}>{selectedBid.notes}</p>
              </Surface>
            )}

            {selectedBid.status === 'accepted' && (
              <Surface padding="var(--lk-space-4)" style={{ borderLeft: '3px solid var(--lk-success-500)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                  <CircleCheck size={24} color="var(--lk-success-500)" />
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--lk-success-600)' }}>Congratulations!</div>
                    <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Your bid was accepted. Next steps will be sent via email.</div>
                  </div>
                </div>
              </Surface>
            )}
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--lk-space-2)', marginTop: 'var(--lk-space-4)' }}>
          {selectedBid?.status === 'pending' && (
            <Button
              variant="danger"
              onClick={() => handleWithdraw(selectedBid.id)}
              loading={withdrawing}
            >
              Withdraw Bid
            </Button>
          )}
          <Button variant="secondary" onClick={() => setDetailsOpen(false)}>Close</Button>
        </div>
      </Modal>

      <FloatingOwenAssistant context="lender-bids" />
      <FloatingKnowledge customContent={{
        label: 'Bids',
        title: 'Bid management',
        body: 'Track and manage your marketplace bids.',
        bullets: ['Monitor bid status', 'Track acceptance rates', 'Adjust pricing strategy'],
        resources: [{ label: 'Bidding strategy', href: '#' }],
      }} />
    </div>
  );
};

export default LenderBids;
